import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
} from "@mui/material";
import ReplayVideo from "../../components/Player/ReplayVideo";
import userService from "../../services/user.service";
import { Helmet } from "react-helmet";

const VideoPlayerScreen = () => {
  const { guid } = useParams();
  const history = useHistory();
  const [video, setVideo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const resolvedGuid = useMemo(() => decodeURIComponent(guid), [guid]);

  useEffect(() => {
    let isMounted = true;

    const fetchVideo = async () => {
      setLoading(true);
      setError(null);

      try {
        const tryFetch = async (payload) => {
          const response = await userService.getVideoByID(payload);

          if (response?.data?.details && response.data.details.length > 0) {
            return response.data.details[0];
          }

          return null;
        };

        let videoDetails = await tryFetch({ videoId: resolvedGuid });

        if (!videoDetails) {
          videoDetails = await tryFetch({ sessionVideoId: resolvedGuid });
        }

        if (!videoDetails) {
          throw new Error("Video not found.");
        }

        if (isMounted) {
          setVideo(videoDetails);
        }
      } catch (err) {
        if (!isMounted) {
          return;
        }

        const message =
          err?.response?.data?.message ||
          err?.message ||
          "Something went wrong while loading the video.";

        setError(message);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchVideo();

    return () => {
      isMounted = false;
    };
  }, [resolvedGuid]);

  const handleBack = () => {
    history.push("/all-videos");
  };

  const instructorName = [video?.firstName, video?.lastName]
    .filter(Boolean)
    .join(" ");
  const videoTitle = video?.videoTitle?.trim();
  const pageTitle = videoTitle
    ? `${videoTitle} | GetUpp Play`
    : "Video Player | GetUpp Play";
  const fallbackDescriptor = video?.videoType
    ? `${video.videoType} session`
    : "GetUpp Play video";
  const defaultDescription = "Stream GetUpp Play wellness videos on demand.";
  const metaDescription = video
    ? video?.notes && video.notes.trim().length > 0
      ? video.notes.trim().slice(0, 155)
      : `Watch ${fallbackDescriptor}${
          instructorName ? ` with ${instructorName}` : ""
        } on GetUpp Play.`
    : defaultDescription;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
      </Helmet>
      <Dialog
        open
        fullScreen
        onClose={handleBack}
        PaperProps={{
          sx: {
            backgroundColor: "#000",
          },
        }}
      >
        <DialogContent
          sx={{
            p: { xs: 2, sm: 3 },
            backgroundColor: "transparent",
          }}
        >
          {loading && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight="50vh"
            >
              <CircularProgress color="inherit" />
            </Box>
          )}

          {!loading && error && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minHeight="40vh"
              textAlign="center"
              sx={{ color: "#fff" }}
            >
              <Alert severity="error" sx={{ mb: 2, width: "100%", maxWidth: 320 }}>
                {error}
              </Alert>
              <Button variant="contained" color="primary" onClick={handleBack}>
                Go back
              </Button>
            </Box>
          )}

          {!loading && !error && video && (
            <ReplayVideo selectedVideo={video} backLink="/all-videos" />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoPlayerScreen;
